import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FamiliaBot } from "@/components/app/FamiliaBot";
import { Link } from "react-router-dom";
import { LIVE_KEY } from "@/pages/FamiliaLiveMatch";
import { buildShareCard, shareCard } from "@/lib/shareCard";
import { PositionPicker } from "@/components/familia/PositionPicker";
import { ImproveSection } from "@/components/familia/ImproveSection";
import { MomentsSection } from "@/components/familia/MomentsSection";
import { EvolutionSection } from "@/components/familia/EvolutionSection";
import { toast } from "sonner";
import {
  Plus, Share2, ExternalLink, Trash2, X, Smartphone, Goal, Clock, Trophy, CalendarDays, Camera, Loader2, BarChart3, Dumbbell, FileText,
} from "lucide-react";

const POSITIONS = [["POR", "Portero/a"], ["DEF", "Defensa"], ["MED", "Centrocampista"], ["DEL", "Delantero/a"]];

const isoWeekKey = (d) => {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return `${dt.getUTCFullYear()}-${Math.ceil(((dt - yearStart) / 86400000 + 1) / 7)}`;
};

export const computeRacha = (matches) => {
  const weeks = new Set();
  for (const j of matches) {
    if (!j.date) continue;
    const d = new Date(j.date.slice(0, 10) + "T12:00:00");
    if (!isNaN(d)) weeks.add(isoWeekKey(d));
  }
  let cur = new Date();
  if (!weeks.has(isoWeekKey(cur))) {
    cur = new Date(cur.getTime() - 7 * 86400000);
    if (!weeks.has(isoWeekKey(cur))) return 0;
  }
  let n = 0;
  while (weeks.has(isoWeekKey(cur))) {
    n += 1;
    cur = new Date(cur.getTime() - 7 * 86400000);
  }
  return n;
};

function PlayerAvatar({ player, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const src = player.photo_url ? `${BACKEND_URL}${player.photo_url}` : null;
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/players/${player.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto actualizada");
      onUploaded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir la foto"); }
    finally { setUploading(false); }
  };
  return (
    <button
      data-testid={`familia-photo-btn-${player.id}`}
      onClick={() => inputRef.current?.click()}
      className="relative w-14 h-14 border border-primary/50 bg-primary/10 flex items-center justify-center font-heading font-black text-primary text-lg overflow-hidden group shrink-0"
      title="Subir o cambiar foto"
    >
      {src ? <img src={src} alt={player.name} className="w-full h-full object-cover" /> : (player.jersey_number ?? player.name?.[0])}
      <span className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
        {uploading ? <Loader2 size={16} className="animate-spin text-white" /> : <Camera size={16} className="text-white" />}
      </span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`familia-photo-input-${player.id}`}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
      />
    </button>
  );
}

function playerTotals(playerId, jornadas) {
  let pj = 0, min = 0, gol = 0, asi = 0;
  for (const j of jornadas) {
    const ps = (j.player_stats || []).find((x) => x.player_id === playerId);
    if (!ps) continue;
    const m = Number(ps.minutos || 0);
    if (j.session_type === "partido" && m > 0) pj += 1;
    min += m;
    gol += Number(ps.gols || 0);
    asi += Number(ps.asistencias || 0);
  }
  return { pj, min, gol, asi };
}

function ProposeClubCard({ players }) {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ club_name: "", city: "", contact_info: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get("/familias/my-club-leads").then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const cn = players?.find((p) => p.club_name)?.club_name;
    if (cn) setForm((f) => (f.club_name ? f : { ...f, club_name: cn }));
  }, [players]);
  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/familias/propose-club", form);
      toast.success("¡Gracias! Hablaremos con el club — te avisaremos si se une 💪");
      setOpen(false);
      const { data } = await api.get("/familias/my-club-leads");
      setLeads(data.leads || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const STATUS = { nuevo: "Recibido — lo revisamos", contactado: "Hemos contactado con el club", cliente: "¡Su club ya está en Sudefootball! 🎉 Tienes 1 mes de regalo", descartado: "De momento no ha sido posible" };
  return (
    <div className="border border-border p-4 space-y-3" data-testid="familia-propose-club-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-black uppercase text-sm">🏟️ ¿Su club aún no usa Sudefootball?</div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-md leading-relaxed">
            Dinos cuál es y hablamos con ellos. Si su club se une, podrás vincular los datos oficiales
            con la ficha de tu peque <b className="text-foreground">y te regalamos 1 mes de Player</b>. 🎁
          </p>
        </div>
        {!open && (
          <button data-testid="familia-propose-club-btn" onClick={() => setOpen(true)}
                  className="h-9 px-4 border border-primary/60 text-primary hover:bg-primary/10 text-[11px] uppercase font-heading font-black tracking-wider transition-colors shrink-0">
            Proponer mi club
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={send} className="flex flex-wrap gap-2">
          <input data-testid="propose-club-name" required className="input-dark flex-1 min-w-[160px]" placeholder="Nombre del club *"
                 value={form.club_name} onChange={(e) => setForm({ ...form, club_name: e.target.value })} />
          <input data-testid="propose-club-city" className="input-dark flex-1 min-w-[120px]" placeholder="Ciudad"
                 value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <input data-testid="propose-club-contact" className="input-dark flex-1 min-w-[180px]" placeholder="Contacto del club (si lo sabes)"
                 value={form.contact_info} onChange={(e) => setForm({ ...form, contact_info: e.target.value })} />
          <button data-testid="propose-club-submit" type="submit" disabled={sending} className="btn-volt h-10 px-4 text-xs disabled:opacity-50">
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </form>
      )}
      {leads.length > 0 && (
        <div className="space-y-1" data-testid="propose-club-status">
          {leads.map((l) => (
            <div key={l.id} className="text-[11px] text-muted-foreground">
              ✓ <b className="text-foreground">{l.club_name}</b> — {STATUS[l.status] || l.status}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PwaBanner() {
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
  const [visible, setVisible] = useState(() => {
    if (isStandalone) return false;
    try { return !localStorage.getItem("familia_pwa_dismissed"); } catch { return true; }
  });
  const [deferred, setDeferred] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (!visible) return null;
  const dismiss = () => { setVisible(false); try { localStorage.setItem("familia_pwa_dismissed", "1"); } catch {} };
  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
      if (outcome === "accepted") dismiss();
      setDeferred(null);
    } else {
      setShowHelp(true);
    }
  };
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  return (
    <>
      <div data-testid="familia-pwa-banner" className="border border-primary/40 bg-primary/5 p-4 flex items-center gap-3">
        <Smartphone size={20} className="text-primary shrink-0" />
        <div className="flex-1 text-xs leading-relaxed">
          <b className="font-heading uppercase">Instala Sudefootball en tu móvil</b>
          <span className="text-muted-foreground"> — apunta los partidos desde la grada en 1 minuto.</span>
        </div>
        <button data-testid="familia-pwa-install-btn" onClick={install} className="btn-volt text-[11px] px-3 h-8 shrink-0">Instalar</button>
        <button data-testid="familia-pwa-dismiss-btn" onClick={dismiss} className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center shrink-0" aria-label="Cerrar"><X size={14} /></button>
      </div>
      {showHelp && (
        <div className="fixed inset-0 z-[80] flex items-end md:items-center justify-center" data-testid="familia-pwa-help-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowHelp(false)} />
          <div className="relative bg-card border border-border w-full max-w-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-black uppercase">Instalar en tu móvil</h3>
              <button onClick={() => setShowHelp(false)} data-testid="familia-pwa-help-close" className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center"><X size={14} /></button>
            </div>
            {isIOS ? (
              <ol className="text-sm space-y-3 list-none">
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">1</span><span>Abre esta página en <b>Safari</b> (no funciona desde Instagram/WhatsApp)</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">2</span><span>Toca el botón <b>Compartir</b> <span className="text-primary">⎋</span> (cuadrado con flecha, abajo en el centro)</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">3</span><span>Baja y elige <b>"Añadir a pantalla de inicio"</b></span></li>
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">4</span><span>Toca <b>"Añadir"</b> — verás el icono de Sudefootball como una app más ⚽</span></li>
              </ol>
            ) : (
              <ol className="text-sm space-y-3 list-none">
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">1</span><span>Abre esta página en <b>Chrome</b> (no desde Instagram/WhatsApp)</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">2</span><span>Toca el menú <b>⋮</b> (arriba a la derecha)</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">3</span><span>Elige <b>"Instalar aplicación"</b> o <b>"Añadir a pantalla de inicio"</b></span></li>
                <li className="flex gap-3"><span className="w-6 h-6 shrink-0 bg-primary text-white font-heading font-black text-xs flex items-center justify-center">4</span><span>Confirma — verás el icono de Sudefootball como una app más ⚽</span></li>
              </ol>
            )}
            <button onClick={() => setShowHelp(false)} className="btn-volt w-full h-11" data-testid="familia-pwa-help-ok">Entendido</button>
          </div>
        </div>
      )}
    </>
  );
}

function AddPlayerModal({ team, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", jersey_number: "", position: "DEL", dominant_foot: "Derecho", birthdate: "", club_name: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Escribe el nombre del jugador/a");
    setSaving(true);
    try {
      await api.post("/players", {
        name: form.name.trim(), team_id: team.id, position: form.position,
        dominant_foot: form.dominant_foot,
        jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
        birthdate: form.birthdate || null,
      });
      if (form.club_name.trim() && form.club_name.trim() !== team.name) {
        try {
          await api.put(`/teams/${team.id}`, {
            name: form.club_name.trim(), category_id: team.category_id,
            coach_ids: team.coach_ids || [], season: team.season,
          });
        } catch { /* no bloquear el alta */ }
      }
      toast.success("Jugador/a creado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center" data-testid="familia-add-player-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-black uppercase">Añadir jugador/a</h3>
          <button onClick={onClose} data-testid="familia-add-player-close" className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center"><X size={14} /></button>
        </div>
        <input data-testid="familia-player-name-input" className="input-dark w-full" placeholder="Nombre y apellido" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div className="grid grid-cols-3 gap-2">
          <input data-testid="familia-player-dorsal-input" type="number" min="1" max="99" className="input-dark" placeholder="Dorsal" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} />
          <select data-testid="familia-player-position-select" className="input-dark" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>
            {POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="familia-player-foot-select" className="input-dark" value={form.dominant_foot} onChange={(e) => setForm({ ...form, dominant_foot: e.target.value })}>
            <option>Derecho</option><option>Zurdo</option><option>Ambidiestro</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label className="space-y-1"><span className="text-muted-foreground">Fecha de nacimiento</span>
            <input data-testid="familia-player-birthdate-input" type="date" className="input-dark w-full" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} /></label>
          <label className="space-y-1"><span className="text-muted-foreground">Su equipo/club real</span>
            <input data-testid="familia-player-club-input" className="input-dark w-full" placeholder="ej. Alevín A · CD Nervión" maxLength={60} value={form.club_name} onChange={(e) => setForm({ ...form, club_name: e.target.value })} /></label>
        </div>
        <p className="text-[10px] text-muted-foreground">El equipo/club real aparecerá en su ficha pública.</p>
        <button data-testid="familia-player-save-btn" onClick={save} disabled={saving} className="btn-volt w-full h-11">{saving ? "Guardando…" : "Guardar"}</button>
      </div>
    </div>
  );
}

const MOTIVOS = [["decision_entrenador", "Decisión del entrenador"], ["lesion", "Lesión"], ["sancion", "Sanción"], ["motivo_personal", "Motivo personal"], ["otro", "Otro"]];
const SIMPLE_STATS = [["gols", "Goles"], ["asistencias", "Asistencias"], ["shots_on_goal", "Remates"]];
const ADV_STATS = [["pases_ok", "Pases buenos"], ["perdidas", "Pérdidas"], ["recuperaciones", "Recuperac."], ["regates", "Regates"], ["duelos_ganados", "Duelos ganados"], ["juego_aereo", "Juego aéreo"], ["intercepciones", "Intercepc."], ["entradas", "Entradas"], ["centros", "Centros"], ["pases_clave", "Pases clave"]];
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="familia-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(value === n ? 0 : n)} className={`text-lg leading-none ${n <= value ? "text-amber-400" : "text-border"}`} aria-label={`${n} estrellas`}>★</button>
      ))}
    </div>
  );
}

function StatInput({ label, value, onChange, testId }) {
  return (
    <label className="space-y-1">
      <span className="text-muted-foreground">{label}</span>
      <input type="number" min="0" placeholder="—" className="input-dark w-full" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </label>
  );
}

function MatchModal({ teamId, players, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [match, setMatch] = useState({ date: today, opponent: "", home_away: "casa", gf: "", gc: "" });
  const [adv, setAdv] = useState(() => { try { return localStorage.getItem("familia_adv_mode") === "1"; } catch { return false; } });
  const [rows, setRows] = useState(() => players.map((p) => ({
    player_id: p.id, name: p.name, convocado: true, motivo: "decision_entrenador", titular: true,
    minutos: "", gols: "", asistencias: "", shots_on_goal: "", amarilla: false, roja: false, valoracion: 0, posicion: null,
    pases_ok: "", perdidas: "", recuperaciones: "", regates: "", duelos_ganados: "",
    juego_aereo: "", intercepciones: "", entradas: "", centros: "", pases_clave: "",
    autoOpen: false, autoNota: "", autoAnimo: 0, autoBien: "", autoMejorar: "",
  })));
  const [saving, setSaving] = useState(false);
  const setRow = (i, patch) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const toggleAdv = () => {
    setAdv((v) => { try { localStorage.setItem("familia_adv_mode", v ? "0" : "1"); } catch {} return !v; });
  };
  const save = async () => {
    if (!match.opponent.trim()) return toast.error("Escribe el nombre del rival");
    const convocados = rows.filter((r) => r.convocado);
    if (convocados.some((r) => r.minutos === "")) return toast.error("Indica los minutos jugados (0 si no llegó a jugar)");
    const gf = Number(match.gf || 0), gc = Number(match.gc || 0);
    setSaving(true);
    try {
      await api.post("/jornadas", {
        team_id: teamId, session_type: "partido", date: match.date,
        opponent: match.opponent.trim(), home_away: match.home_away, status: "final",
        team_stats: { gols_favor: gf, gols_contra: gc, resultado: gf > gc ? "V" : gf === gc ? "E" : "D" },
        player_stats: rows.map((r) => r.convocado ? {
          player_id: r.player_id, convocado: true, titular: r.titular,
          minutos: Number(r.minutos || 0),
          posicion: r.posicion || null,
          gols: num(r.gols), asistencias: num(r.asistencias), shots_on_goal: num(r.shots_on_goal),
          targetes_grogues: r.amarilla || r.roja ? (r.amarilla ? 1 : 0) : null,
          targetes_roges: r.amarilla || r.roja ? (r.roja ? 1 : 0) : null,
          valoracion_familiar: r.valoracion || null,
          autoeval_nota: num(r.autoNota), autoeval_animo: r.autoAnimo || null,
          autoeval_bien: r.autoBien.trim() || null, autoeval_mejorar: r.autoMejorar.trim() || null,
          pases_ok: num(r.pases_ok), perdidas: num(r.perdidas), recuperaciones: num(r.recuperaciones),
          regates: num(r.regates), duelos_ganados: num(r.duelos_ganados),
          juego_aereo: num(r.juego_aereo), intercepciones: num(r.intercepciones),
          entradas: num(r.entradas), centros: num(r.centros), pases_clave: num(r.pases_clave),
        } : {
          player_id: r.player_id, convocado: false, no_convocado_motivo: r.motivo, minutos: 0,
        }),
      });
      toast.success("Partido guardado ⚽");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center" data-testid="familia-match-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border w-full max-w-lg p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-black uppercase">Apuntar partido</h3>
          <button onClick={onClose} data-testid="familia-match-close" className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center"><X size={14} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="familia-match-date" type="date" className="input-dark" value={match.date} onChange={(e) => setMatch({ ...match, date: e.target.value })} />
          <select data-testid="familia-match-homeaway" className="input-dark" value={match.home_away} onChange={(e) => setMatch({ ...match, home_away: e.target.value })}>
            <option value="casa">En casa</option><option value="fora">Fuera</option>
          </select>
        </div>
        <input data-testid="familia-match-opponent" className="input-dark w-full" placeholder="Rival (ej. CD Los Olivos)" value={match.opponent} onChange={(e) => setMatch({ ...match, opponent: e.target.value })} />
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground uppercase font-heading shrink-0">Resultado</span>
          <input data-testid="familia-match-gf" type="number" min="0" className="input-dark w-20 text-center" placeholder="Su equipo" value={match.gf} onChange={(e) => setMatch({ ...match, gf: e.target.value })} />
          <span className="font-heading font-black">-</span>
          <input data-testid="familia-match-gc" type="number" min="0" className="input-dark w-20 text-center" placeholder="Rival" value={match.gc} onChange={(e) => setMatch({ ...match, gc: e.target.value })} />
          <span className="text-[10px] text-muted-foreground">goles de su equipo — rival</span>
        </div>
        <div className="text-[10px] text-muted-foreground leading-relaxed border border-border p-2">
          💡 Rellena solo lo que sepas: lo que dejes vacío queda como <b>"sin registrar"</b>, no como cero.
        </div>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={r.player_id} className="border border-border p-3 space-y-3" data-testid={`familia-match-row-${i}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-heading font-bold text-sm uppercase">{r.name}</span>
                <label className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={r.convocado} onChange={(e) => setRow(i, { convocado: e.target.checked })} data-testid={`familia-match-convocado-${i}`} />
                  Convocado/a
                </label>
              </div>
              {!r.convocado ? (
                <label className="block text-xs space-y-1">
                  <span className="text-muted-foreground">¿Por qué no fue convocado/a?</span>
                  <select className="input-dark w-full" value={r.motivo} onChange={(e) => setRow(i, { motivo: e.target.value })} data-testid={`familia-match-motivo-${i}`}>
                    {MOTIVOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <label className="space-y-1">
                      <span className="text-muted-foreground">Titular / suplente</span>
                      <select className="input-dark w-full" value={r.titular ? "titular" : "suplente"} onChange={(e) => setRow(i, { titular: e.target.value === "titular" })} data-testid={`familia-match-titular-${i}`}>
                        <option value="titular">Titular</option><option value="suplente">Suplente</option>
                      </select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-muted-foreground">Minutos jugados *</span>
                      <input type="number" min="0" max="120" className="input-dark w-full" placeholder="ej. 60" value={r.minutos} onChange={(e) => setRow(i, { minutos: e.target.value })} data-testid={`familia-match-min-${i}`} />
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {SIMPLE_STATS.map(([k, l]) => (
                      <StatInput key={k} label={l} value={r[k]} onChange={(v) => setRow(i, { [k]: v })} testId={`familia-match-${k}-${i}`} />
                    ))}
                  </div>
                  <div className="pt-1">
                    <PositionPicker value={r.posicion} onChange={(pos) => setRow(i, { posicion: pos })} testId={`familia-match-pos-${i}`} />
                  </div>
                  {adv && (
                    <div className="grid grid-cols-3 gap-2 text-xs" data-testid={`familia-match-adv-${i}`}>
                      {ADV_STATS.map(([k, l]) => (
                        <StatInput key={k} label={l} value={r[k]} onChange={(v) => setRow(i, { [k]: v })} testId={`familia-match-${k}-${i}`} />
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" checked={r.amarilla} onChange={(e) => setRow(i, { amarilla: e.target.checked })} /> <span className="text-amber-400">Amarilla</span>
                      <input type="checkbox" className="ml-3" checked={r.roja} onChange={(e) => setRow(i, { roja: e.target.checked })} /> <span className="text-red-400">Roja</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-[10px] uppercase">Valoración familiar</span>
                      <Stars value={r.valoracion} onChange={(v) => setRow(i, { valoracion: v })} />
                    </div>
                  </div>
                  <button type="button" onClick={() => setRow(i, { autoOpen: !r.autoOpen })} data-testid={`familia-autoeval-toggle-${i}`} className="w-full h-8 border border-dashed border-border hover:border-primary text-[10px] uppercase font-heading tracking-wider text-muted-foreground hover:text-primary transition-colors">
                    {r.autoOpen ? "− Autoevaluación del jugador" : "+ Autoevaluación del jugador (pregúntale a él/ella)"}
                  </button>
                  {r.autoOpen && (
                    <div className="space-y-2 text-xs border border-border p-2" data-testid={`familia-autoeval-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">¿Cómo crees que has jugado? (1-10)</span>
                        <input type="number" min="1" max="10" className="input-dark w-16 text-center" placeholder="—" value={r.autoNota} onChange={(e) => setRow(i, { autoNota: e.target.value })} data-testid={`familia-autoeval-nota-${i}`} />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">¿Cómo te has sentido?</span>
                        <div className="flex gap-1" data-testid={`familia-autoeval-animo-${i}`}>
                          {["😟", "😐", "🙂", "😃", "🔥"].map((e, idx) => (
                            <button key={idx} type="button" data-testid={`familia-autoeval-animo-${i}-${idx + 1}`} onClick={() => setRow(i, { autoAnimo: r.autoAnimo === idx + 1 ? 0 : idx + 1 })} className={`text-lg leading-none px-0.5 ${r.autoAnimo === idx + 1 ? "" : "opacity-35"}`}>{e}</button>
                          ))}
                        </div>
                      </div>
                      <input className="input-dark w-full" placeholder="¿De qué estás más satisfecho/a?" maxLength={300} value={r.autoBien} onChange={(e) => setRow(i, { autoBien: e.target.value })} data-testid={`familia-autoeval-bien-${i}`} />
                      <input className="input-dark w-full" placeholder="¿Qué quieres mejorar?" maxLength={300} value={r.autoMejorar} onChange={(e) => setRow(i, { autoMejorar: e.target.value })} data-testid={`familia-autoeval-mejorar-${i}`} />
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={toggleAdv} data-testid="familia-adv-toggle" className="w-full h-9 border border-dashed border-border hover:border-primary text-[11px] uppercase font-heading tracking-wider text-muted-foreground hover:text-primary transition-colors">
          {adv ? "− Ocultar modo avanzado" : "+ Modo avanzado (pases, regates, duelos…)"}
        </button>
        <button data-testid="familia-match-save-btn" onClick={save} disabled={saving} className="btn-volt w-full h-11">{saving ? "Guardando…" : "Guardar partido"}</button>
      </div>
    </div>
  );
}

const ESTADOS_ENTRENO = [["completo", "Entrenó completo"], ["parcial", "Entrenó parcial"], ["ausencia", "No fue"], ["lesionado", "Lesionado/a"]];

function TrainingModal({ teamId, players, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState(() => players.map((p) => ({ player_id: p.id, name: p.name, estado: "completo", duracion: "90" })));
  const [saving, setSaving] = useState(false);
  const setRow = (i, patch) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/attendance", {
        team_id: teamId, date,
        attendance: rows.map((r) => ({
          player_id: r.player_id, estado: r.estado,
          duracion_min: r.estado === "ausencia" ? null : num(r.duracion),
        })),
      });
      toast.success("Entrenamiento guardado 💪");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center" data-testid="familia-training-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border w-full max-w-md p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-black uppercase">Apuntar entrenamiento</h3>
          <button onClick={onClose} data-testid="familia-training-close" className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center"><X size={14} /></button>
        </div>
        <input data-testid="familia-training-date" type="date" className="input-dark w-full" value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={r.player_id} className="border border-border p-3 space-y-2" data-testid={`familia-training-row-${i}`}>
              <div className="font-heading font-bold text-sm uppercase">{r.name}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <select className="input-dark" value={r.estado} onChange={(e) => setRow(i, { estado: e.target.value })} data-testid={`familia-training-estado-${i}`}>
                  {ESTADOS_ENTRENO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {r.estado !== "ausencia" && (
                  <input type="number" min="0" max="300" className="input-dark" placeholder="Duración (min)" value={r.duracion} onChange={(e) => setRow(i, { duracion: e.target.value })} data-testid={`familia-training-dur-${i}`} />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-muted-foreground">Si registras el mismo día dos veces, se actualiza (no se duplica).</div>
        <button data-testid="familia-training-save-btn" onClick={save} disabled={saving} className="btn-volt w-full h-11">{saving ? "Guardando…" : "Guardar entrenamiento"}</button>
      </div>
    </div>
  );
}

function BenchmarkRow({ label, mine, avg, decimals = 2 }) {
  const max = Math.max(mine, avg, 0.01);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px]">
        <span className="uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`font-mono font-bold ${mine >= avg ? "text-emerald-400" : "text-muted-foreground"}`}>
          {mine.toFixed(decimals)} <span className="text-muted-foreground font-normal">vs {avg.toFixed(decimals)} media</span>
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 h-2 bg-border/40"><div className="h-full bg-primary" style={{ width: `${(100 * mine) / max}%` }} /></div>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 h-2 bg-border/40"><div className="h-full bg-muted-foreground/40" style={{ width: `${(100 * avg) / max}%` }} /></div>
      </div>
    </div>
  );
}

function PlayerBenchmark({ playerId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const loadBench = async () => {
    if (data) { setOpen((v) => !v); return; }
    try {
      const { data: d } = await api.get(`/familias/benchmark/${playerId}`);
      setData(d);
      setOpen(true);
    } catch { toast.error("No se pudo cargar la comparativa"); }
  };
  const show = data && data.player.pj > 0 && data.category_avg.sample >= 3;
  return (
    <div>
      <button
        data-testid={`familia-benchmark-btn-${playerId}`}
        onClick={loadBench}
        className="w-full h-9 border border-border hover:border-primary text-[11px] uppercase font-heading tracking-wider text-muted-foreground hover:text-primary inline-flex items-center justify-center gap-1.5 transition-colors"
      >
        <BarChart3 size={12} /> {open ? "Ocultar comparativa" : "Comparar con su categoría"}
      </button>
      {open && data && (
        <div className="mt-3 space-y-3 border border-border p-3" data-testid={`familia-benchmark-panel-${playerId}`}>
          {show ? (
            <>
              <BenchmarkRow label="Goles / partido" mine={data.player.gols_pm} avg={data.category_avg.gols_pm} />
              <BenchmarkRow label="Asistencias / partido" mine={data.player.asist_pm} avg={data.category_avg.asist_pm} />
              <BenchmarkRow label="Minutos / partido" mine={data.player.min_pm} avg={data.category_avg.min_pm} decimals={0} />
              <div className="text-[9px] text-muted-foreground leading-relaxed">
                <span className="inline-block w-2 h-2 bg-primary mr-1 align-middle" /> Tu jugador/a ·{" "}
                <span className="inline-block w-2 h-2 bg-muted-foreground/40 mr-1 align-middle" /> Media anónima de {data.category_avg.sample} jugadores de su categoría en Sudefootball
              </div>
            </>
          ) : (
            <div className="text-[11px] text-muted-foreground">
              {data.player.pj === 0 ? "Apunta su primer partido para ver la comparativa." : "Aún no hay suficientes jugadores de su categoría para comparar."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FamiliaDashboard() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [showTraining, setShowTraining] = useState(false);
  const [trainings, setTrainings] = useState([]);
  const [addingSeat, setAddingSeat] = useState(false);
  const [showAllMatches, setShowAllMatches] = useState(false);

  const load = async () => {
    try {
      const [{ data: teams }, { data: plan }] = await Promise.all([
        api.get("/teams"), api.get("/clubs/me/plan"),
      ]);
      const t = teams[0] || null;
      setTeam(t);
      setSeats(plan.seats ?? 8);
      if (t) {
        const [{ data: pls }, { data: js }, { data: tr }] = await Promise.all([
          api.get(`/players?team_id=${t.id}`), api.get(`/jornadas?team_id=${t.id}`), api.get(`/attendance?team_id=${t.id}`),
        ]);
        setPlayers(pls);
        setJornadas(js.filter((j) => j.session_type === "partido"));
        setTrainings(tr);
      }
    } catch { toast.error("No se pudieron cargar tus datos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const share = async (p) => {
    try {
      let token = p.public_token;
      if (!token) {
        const { data } = await api.post(`/players/${p.id}/public-link`);
        token = data.public_token;
      }
      const url = `${window.location.origin}/p/${token}`;
      if (navigator.share) { await navigator.share({ title: `Ficha de ${p.name}`, url }); }
      else { await navigator.clipboard.writeText(url); toast.success("Enlace copiado — compártelo con quien quieras"); }
      load();
    } catch { toast.error("No se pudo generar el enlace"); }
  };

  const deleteMatch = async (id) => {
    if (!window.confirm("¿Borrar este partido?")) return;
    try { await api.delete(`/jornadas/${id}`); toast.success("Partido borrado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo borrar"); }
  };

  const deleteTraining = async (id) => {
    if (!window.confirm("¿Borrar este entrenamiento?")) return;
    try { await api.delete(`/attendance/${id}`); toast.success("Entrenamiento borrado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo borrar"); }
  };

  const shareMatchCard = async (j) => {
    const ts = j.team_stats || {};
    const played = (j.player_stats || []).filter((ps) => ps.convocado !== false && Number(ps.minutos || 0) > 0);
    if (!played.length) return toast.error("Ese partido no tiene minutos registrados");
    let ps = played[0];
    if (played.length > 1) {
      const names = played.map((x, i) => `${i + 1}. ${players.find((p) => p.id === x.player_id)?.name || "?"}`).join("\n");
      const sel = window.prompt(`¿De quién es la tarjeta?\n${names}`, "1");
      ps = played[Number(sel) - 1] || played[0];
    }
    const pl = players.find((p) => p.id === ps.player_id);
    if (!pl) return;
    const blob = await buildShareCard({
      playerName: pl.name, dorsal: pl.jersey_number,
      titular: ps.titular, valoracion: ps.valoracion_familiar, posicion: ps.posicion,
      tg: ps.targetes_grogues || 0, tr: ps.targetes_roges || 0,
      result: { gf: ts.gols_favor ?? 0, gc: ts.gols_contra ?? 0 },
      opponent: j.opponent || "—", homeAway: j.home_away, date: j.date,
      stats: { min: ps.minutos || 0, gols: ps.gols || 0, asist: ps.asistencias || 0, remates: ps.shots_on_goal || 0,
               pases: ps.pases_ok || 0, regates: ps.regates || 0, duelos: ps.duelos_ganados || 0, recup: ps.recuperaciones || 0,
               claves: ps.pases_clave || 0, perdidas: ps.perdidas || 0, aereos: ps.juego_aereo || 0,
               interc: ps.intercepciones || 0, entradas: ps.entradas || 0, centros: ps.centros || 0 },
      season: playerTotals(pl.id, jornadas),
    });
    const r = await shareCard(blob, pl.name);
    if (r === "downloaded") toast.success("Tarjeta descargada — adjúntala en WhatsApp 📲");
  };

  const addSeat = async () => {
    if (!window.confirm("Vas a ampliar tu suscripción en 1 jugador más: +12€/año (Stripe te cobra ahora la parte proporcional hasta tu renovación). ¿Continuar?")) return;
    setAddingSeat(true);
    try {
      const { data } = await api.post("/familias/add-seat");
      setSeats(data.seats);
      toast.success(`¡Listo! Ahora puedes seguir a ${data.seats} jugadores`);
      setShowAdd(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo ampliar la suscripción", { duration: 8000 });
    } finally { setAddingSeat(false); }
  };

  const downloadReport = async () => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const options = [now, prev].map((d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    const month = window.prompt(`¿De qué mes quieres el informe? (YYYY-MM)\nEj: ${options[0]} (este mes) o ${options[1]} (anterior)`, options[1]);
    if (!month) return;
    try {
      const resp = await api.get(`/familias/informe.pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `informe-${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el informe (formato YYYY-MM)"); }
  };

  const liveInProgress = (() => { try { const s = JSON.parse(localStorage.getItem(LIVE_KEY) || "null"); return s && s.phase !== "setup"; } catch { return false; } })();

  const canAdd = players.length < (seats ?? 8);
  const matches = useMemo(() => [...jornadas].sort((a, b) => (b.date || "").localeCompare(a.date || "")), [jornadas]);
  const racha = useMemo(() => computeRacha(jornadas), [jornadas]);

  if (loading) return <div className="py-20 text-center text-muted-foreground text-sm" data-testid="familia-loading">Cargando…</div>;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="familia-dashboard">
      <PwaBanner />
      {liveInProgress && (
        <Link to="/familia/directo" data-testid="familia-live-resume" className="block border-2 border-primary bg-primary/10 p-3 text-center font-heading font-black uppercase text-sm text-primary animate-pulse">
          🔴 Tienes un partido en directo — Continuar
        </Link>
      )}

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-primary">Sudefootball Player</div>
          <h1 className="font-heading font-black uppercase text-2xl md:text-3xl leading-none mt-1">Hola, {user?.name?.split(" ")[0]}</h1>
          {racha >= 1 && (
            <div data-testid="familia-racha-badge" className="mt-2 inline-flex items-center gap-1.5 border border-amber-500/60 bg-amber-500/10 text-amber-500 px-2.5 py-1 text-[11px] uppercase font-heading font-black tracking-wider" title="Semanas seguidas apuntando al menos un partido">
              🔥 Racha: {racha} semana{racha !== 1 ? "s" : ""} seguidas
            </div>
          )}
        </div>
        {players.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <button data-testid="familia-report-btn" onClick={downloadReport} className="h-11 px-3 border border-border hover:border-primary text-muted-foreground hover:text-primary text-xs uppercase font-heading tracking-wider inline-flex items-center gap-1.5 transition-colors" title="Descargar informe mensual PDF">
              <FileText size={14} /> Informe
            </button>
            <button data-testid="familia-new-training-btn" onClick={() => setShowTraining(true)} className="h-11 px-4 border border-primary/60 text-primary hover:bg-primary/10 text-xs uppercase font-heading font-black tracking-wider inline-flex items-center gap-2 transition-colors">
              <Dumbbell size={15} /> Entreno
            </button>
            <button data-testid="familia-new-match-btn" onClick={() => setShowMatch(true)} className="btn-volt h-11 px-5 inline-flex items-center gap-2">
              <Plus size={16} /> Apuntar partido
            </button>
            <Link to="/familia/directo" data-testid="familia-live-btn" className="h-11 px-4 border-2 border-primary text-primary hover:bg-primary hover:text-white text-xs uppercase font-heading font-black tracking-wider inline-flex items-center gap-1.5 transition-colors">
              🔴 En directo
            </Link>
          </div>
        )}
      </div>

      {/* Jugadores */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-black uppercase text-sm text-muted-foreground">Tus jugadores ({players.length}/{seats ?? 8})</h2>
          {canAdd ? (
            <button data-testid="familia-add-player-btn" onClick={() => setShowAdd(true)} className="text-xs uppercase font-heading tracking-wider text-primary hover:underline inline-flex items-center gap-1">
              <Plus size={12} /> Añadir
            </button>
          ) : (seats ?? 0) < 8 && (
            <button data-testid="familia-add-seat-btn" onClick={addSeat} disabled={addingSeat} className="text-xs uppercase font-heading tracking-wider text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50">
              {addingSeat ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Añadir otro · +12€/año
            </button>
          )}
        </div>
        {players.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center space-y-3" data-testid="familia-empty-state">
            <div className="text-3xl">⚽</div>
            <p className="text-sm text-muted-foreground">Crea la ficha de tu hijo/a para empezar a apuntar sus partidos.</p>
            <button data-testid="familia-empty-add-btn" onClick={() => setShowAdd(true)} className="btn-volt h-11 px-6">Crear su ficha</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {players.map((p) => {
              const s = playerTotals(p.id, jornadas);
              return (
                <div key={p.id} className="bg-card border border-border p-4 space-y-3" data-testid={`familia-player-card-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <PlayerAvatar player={p} onUploaded={load} />
                    <div className="min-w-0">
                      <div className="font-heading font-black uppercase truncate">{p.name}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{POSITIONS.find(([v]) => v === p.position)?.[1] || p.position} · {p.dominant_foot}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {[["PJ", s.pj, Trophy], ["Goles", s.gol, Goal], ["Asist.", s.asi, Share2], ["Min.", s.min, Clock]].map(([l, v]) => (
                      <div key={l} className="border border-border py-2">
                        <div className="font-heading font-black text-lg leading-none">{v}</div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">{l}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider text-center -mt-1">Totales de todas las temporadas</div>
                  <div className="flex gap-2">
                    <button data-testid={`familia-share-btn-${p.id}`} onClick={() => share(p)} className="flex-1 h-9 border border-primary/50 text-primary hover:bg-primary/10 text-[11px] uppercase font-heading tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors">
                      <Share2 size={12} /> Compartir ficha
                    </button>
                    {p.public_token && (
                      <a data-testid={`familia-view-btn-${p.id}`} href={`/p/${p.public_token}`} target="_blank" rel="noreferrer" className="h-9 px-3 border border-border hover:border-primary text-[11px] uppercase font-heading tracking-wider inline-flex items-center gap-1.5 transition-colors">
                        <ExternalLink size={12} /> Ver
                      </a>
                    )}
                  </div>
                  <PlayerBenchmark playerId={p.id} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Partidos */}
      {matches.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-black uppercase text-sm text-muted-foreground">Últimos partidos</h2>
          <div className="divide-y divide-border border border-border" data-testid="familia-matches-list">
            {matches.slice(0, showAllMatches ? matches.length : 15).map((j) => {
              const ts = j.team_stats || {};
              const res = ts.resultado || "";
              return (
                <div key={j.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`familia-match-item-${j.id}`}>
                  <span className={`w-7 h-7 shrink-0 flex items-center justify-center font-heading font-black text-xs border ${res === "V" ? "border-emerald-500 text-emerald-400" : res === "E" ? "border-amber-400 text-amber-400" : "border-red-500 text-red-400"}`}>{res}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold truncate">{ts.gols_favor}-{ts.gols_contra} vs {j.opponent}</div>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <CalendarDays size={10} /> {j.date} · {j.home_away === "casa" ? "En casa" : "Fuera"}
                      {(j.player_stats || []).map((ps) => {
                        const pl = players.find((x) => x.id === ps.player_id);
                        if (!pl) return null;
                        if (ps.convocado === false) {
                          const mot = MOTIVOS.find(([v]) => v === ps.no_convocado_motivo)?.[1] || "no convocado";
                          return <span key={ps.player_id}>· {pl.name.split(" ")[0]}: sin convocar ({mot.toLowerCase()})</span>;
                        }
                        return <span key={ps.player_id}>· {pl.name.split(" ")[0]}: {ps.gols ?? "—"}⚽ {ps.minutos}'{ps.posicion ? ` 📍${ps.posicion}` : ""}{ps.valoracion_familiar ? ` ★${ps.valoracion_familiar}` : ""}</span>;
                      })}
                    </div>
                  </div>
                  <button data-testid={`familia-match-share-${j.id}`} onClick={() => shareMatchCard(j)} className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center shrink-0 transition-colors" aria-label="Compartir tarjeta del partido" title="Tarjeta para WhatsApp">
                    <Share2 size={13} />
                  </button>
                  <button data-testid={`familia-match-delete-${j.id}`} onClick={() => deleteMatch(j.id)} className="w-8 h-8 border border-border hover:border-red-500 hover:text-red-400 flex items-center justify-center shrink-0 transition-colors" aria-label="Borrar partido">
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
          {matches.length > 15 && (
            <button
              data-testid="familia-matches-toggle-btn"
              onClick={() => setShowAllMatches((v) => !v)}
              className="w-full h-9 border border-border hover:border-primary text-[11px] uppercase font-heading tracking-wider text-muted-foreground hover:text-primary transition-colors"
            >
              {showAllMatches ? "Ver menos" : `Ver todos (${matches.length})`}
            </button>
          )}
        </div>
      )}

      {/* Evolución + Álbum + Mejora individual */}
      {players.length > 0 && jornadas.length > 0 && <EvolutionSection players={players} jornadas={jornadas} />}
      {players.length > 0 && <MomentsSection players={players} />}
      {players.length > 0 && <ImproveSection players={players} />}

      {/* Entrenamientos */}
      {trainings.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-black uppercase text-sm text-muted-foreground">Últimos entrenamientos</h2>
          <div className="divide-y divide-border border border-border" data-testid="familia-trainings-list">
            {[...trainings].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 6).map((t) => (
              <div key={t.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`familia-training-item-${t.id}`}>
                <Dumbbell size={14} className="text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-xs">{t.date}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {(t.attendance || []).map((a) => {
                      const pl = players.find((x) => x.id === a.player_id);
                      if (!pl) return null;
                      const est = ESTADOS_ENTRENO.find(([v]) => v === a.estado)?.[1] || (a.present ? "Entrenó" : "No fue");
                      return <span key={a.player_id}>{pl.name.split(" ")[0]}: {est.toLowerCase()}{a.duracion_min ? ` (${a.duracion_min}')` : ""} </span>;
                    })}
                  </div>
                </div>
                <button data-testid={`familia-training-delete-${t.id}`} onClick={() => deleteTraining(t.id)} className="w-8 h-8 border border-border hover:border-red-500 hover:text-red-400 flex items-center justify-center shrink-0 transition-colors" aria-label="Borrar entrenamiento">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ProposeClubCard players={players} />

      {showAdd && team && <AddPlayerModal team={team} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {showMatch && team && <MatchModal teamId={team.id} players={players} onClose={() => setShowMatch(false)} onSaved={() => { setShowMatch(false); load(); }} />}
      {showTraining && team && <TrainingModal teamId={team.id} players={players} onClose={() => setShowTraining(false)} onSaved={() => { setShowTraining(false); load(); }} />}
      <FamiliaBot context="panel" />
    </div>
  );
}
